import { cx } from 'class-variance-authority';
import { getImageProps } from 'next/image';
import { useTranslations } from 'next-intl';

import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';
import { useImageExperience } from '@/app/hooks/useImageExperience';

import MobileFeature from './MobileFeature';

const HostingParty = () => {
  const t = useTranslations();
  const imageExperienceDesktop = useImageExperience({
    japaneseImgObj: {
      img: 'ja-regional/hp-features-hosting-a-party-desktop',
      alt: 'family_table_img_alt',
    },
    jadeImgObj: {
      img: 'features/hosting_a_party_jade',
      alt: 'jade_pdp_petal_primary_image_alt',
    },
    defaultImgObj: {
      img: 'blue-sky/home/host-party-primary',
      alt: 'tab_item_host_party_img_1_aria_label',
    },
  });

  const imageExperienceMobile = useImageExperience({
    japaneseImgObj: {
      img: 'ja-regional/hp-features-hosting-a-party-mobile',
      alt: 'family_table_img_alt',
    },
    jadeImgObj: {
      img: 'jade/hosting_a_party_mobile',
      alt: 'jade_pdp_petal_primary_image_alt',
    },
    defaultImgObj: {
      img: 'blue-sky/home/host-party-primary',
      alt: 'tab_item_host_party_img_1_aria_label',
    },
  });

  const imageExperienceSecondaryImage = useImageExperience({
    japaneseImgObj: {
      img: 'ja-regional/hp-features-hosting-a-party-food',
      alt: 'close_up_food_img_alt',
    },
    jadeImgObj: {
      img: 'jade/hosting_a_party_secondary',
      alt: 'jade_pdp_petal_primary_image_alt',
    },
    defaultImgObj: {
      img: 'blue-sky/home/host-party-secondary',
      alt: 'tab_item_host_party_img_2_aria_label',
    },
  });

  const { props: stressMaskImageProps } = getImageProps({
    alt: '',
    height: 249,
    src: `jade/masks/bg_mask_hosting_a_party_stress.jpg`,
    width: 300,
  });

  const { props: heartRateMaskImageProps } = getImageProps({
    alt: '',
    height: 262,
    src: `jade/masks/bg_mask_hosting_a_party_heart_rate.jpg`,
    width: 345,
  });

  return (
    <MobileFeature
      title="features_tab_5_label"
      image={{
        alt: imageExperienceMobile.alt,
        src: imageExperienceMobile.img,
      }}
    >
      <div className="relative hidden lg:block" data-cy="hosting-party">
        <Image
          alt={t(imageExperienceDesktop.alt)}
          src={imageExperienceDesktop.img}
          height={431}
          width={575}
          className="rounded-lg object-cover"
          data-cy="hosting-party-image"
        />
      </div>
      <div
        className={cx(
          'absolute bottom-16 left-1/2 w-[80vw] max-w-[300px] -translate-x-1/2 flex-col gap-y-6',
          'lg:relative lg:bottom-auto lg:left-0 lg:flex lg:w-auto lg:max-w-none lg:translate-x-0',
        )}
      >
        <div className="hidden gap-6 lg:flex">
          <div
            className="grid grid-cols-1 grid-rows-1 self-end overflow-hidden rounded-lg bg-cover"
            style={{ backgroundImage: `url(${stressMaskImageProps.src})` }}
            data-cy="hosting-party-mask-image-stress"
          >
            <Image
              localizedAlt="daytime_stress_image_alt"
              className="col-start-1 col-end-2 row-start-1 row-end-2 bg-gray-500/30"
              localizedSrc="home_scenarios_party_stress_ui"
              format="png"
              width={300}
              height={249}
              data-cy="party_stress_app_ui_image"
            />
          </div>
          <div>
            <Image
              alt={t(imageExperienceSecondaryImage.alt)}
              className="rounded-lg"
              src={imageExperienceSecondaryImage.img}
              width={220}
              height={294}
              data-cy="hosting-party-secondary-image"
            />
          </div>
        </div>
        <div
          className="hidden w-fit grid-cols-1 grid-rows-1 overflow-hidden rounded-lg bg-cover lg:grid"
          style={{ backgroundImage: `url(${heartRateMaskImageProps.src})` }}
          data-cy="hosting-party-mask-image-heart-rate"
        >
          <Image
            localizedAlt="daytime_heartrate_image_alt"
            className="col-start-1 col-end-2 row-start-1 row-end-2 self-center"
            localizedSrc="home_scenarios_party_hr_ui"
            format="png"
            width={345}
            height={262}
            data-cy="party_heart_rate_app_ui_image"
          />
        </div>
        <Motion
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, type: 'tween', delay: 0.5 }}
          className="grid grid-cols-1 grid-rows-1 overflow-hidden rounded-lg bg-cover lg:hidden"
          style={{ backgroundImage: `url(${heartRateMaskImageProps.src})` }}
          data-cy="hosting-party-mask-image-heart-rate-mobile"
        >
          <Image
            localizedAlt="daytime_heartrate_image_alt"
            className="col-start-1 col-end-2 row-start-1 row-end-2 self-center"
            localizedSrc="home_scenarios_party_hr_ui"
            format="png"
            width={300}
            height={252}
            data-cy="party_heart_rate_app_ui_image"
          />
        </Motion>
      </div>
    </MobileFeature>
  );
};

export default HostingParty;
